import type { ClaimDepartment } from '@evogard/domain';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Alert, Button, Toast, showToast } from '@evogard/ui';
import { deskOf } from './desk.js';
import { useDeskMutations } from './useDesk.js';

/** Quanto tempo o "foi para…" fica na tela com o Desfazer — decisão do dono em 05/10/2026. */
export const UNDO_MS = 7_000;

/** O movimento que acabou de acontecer, e que ainda dá para desfazer. */
export interface LastMove {
  claimId: number;
  /** A mesa de onde o movimento saiu — é a rota dela que desfaz. */
  department: ClaimDepartment;
  toLabel: string;
  passageId: number;
  /**
   * O destino é uma fase de desfecho (`claimClosureKindOf`): o sinistro saiu
   * do quadro e foi para Encerrados (13/10/2026), e o aviso diz onde achá-lo.
   * O "Ver em Encerrados" vai só no canto da tela: com a mesa aberta, o
   * painel de movimento dela já o mostra (`ClosedNotice`), e dois botões
   * iguais empilhados foi o que o percurso de 14/10 achou.
   */
  closed: boolean;
}

/** "#123 foi para Encerrados · Concluídos", ou "#123 foi para Documentação". */
function movedTitle(move: LastMove): string {
  return move.closed
    ? `#${move.claimId} foi para Encerrados · ${move.toLabel}`
    : `#${move.claimId} foi para ${move.toLabel}`;
}

/** O "Ver" ao lado do Desfazer, quando o sinistro foi para Encerrados. */
function SeeClosed({ onSee }: { onSee?: () => void }) {
  const navigate = useNavigate();
  return (
    <Button
      size="sm"
      variant="discreto"
      className="mt-1"
      onClick={() => {
        onSee?.();
        void navigate({ to: '/claims/closed' });
      }}
    >
      Ver em Encerrados
    </Button>
  );
}

/**
 * O Desfazer do movimento, em dois lugares:
 *
 * - **com a mesa aberta** — ela fica no sinistro que se moveu (decisão do dono
 *   em 06/10/2026) —, dentro dela (`UndoMoveNotice`). Fora do modal não
 *   serviria: o `Dialog` do Radix tira o clique de tudo o que está fora dele,
 *   e o aviso no canto da tela aparecia e não respondia — medido no percurso
 *   de 05/10/2026;
 * - **com a mesa fechada** antes de o aviso sumir, no canto da tela
 *   (`showUndoToast`): fechar não custa o Desfazer.
 *
 * Os dois somem sozinhos em `UNDO_MS`. Desfazer é uma passagem NOVA de volta —
 * nada é apagado —, e só vale para quem moveu, na passagem mais recente.
 */
export function UndoMoveNotice({ move, onDone }: { move: LastMove; onDone: () => void }) {
  const mutations = useDeskMutations(deskOf(move.department), move.claimId);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (error) return;
    const timer = setTimeout(onDone, UNDO_MS);
    return () => clearTimeout(timer);
  }, [move, onDone, error]);

  return (
    <Alert tone={error ? 'erro' : 'sucesso'} title={movedTitle(move)}>
      {error ? (
        <p>{error}</p>
      ) : (
        <div className="-ml-2 flex flex-wrap gap-1">
          <Button
            size="sm"
            variant="discreto"
            className="mt-1"
            loading={mutations.undo.isPending}
            onClick={() =>
              mutations.undo.mutate(move.passageId, {
                onSuccess: onDone,
                onError: (e) => setError(e.message),
              })
            }
          >
            Desfazer
          </Button>
        </div>
      )}
    </Alert>
  );
}

/** O mesmo aviso, no canto da tela — quando a mesa fechou. */
export function showUndoToast(move: LastMove): void {
  showToast((dismiss) => <UndoMoveToast move={move} dismiss={dismiss} />, UNDO_MS);
}

function UndoMoveToast({ move, dismiss }: { move: LastMove; dismiss: () => void }) {
  const mutations = useDeskMutations(deskOf(move.department), move.claimId);
  const [error, setError] = useState<string | null>(null);
  return (
    <Toast
      title={error ? 'Não deu para desfazer' : movedTitle(move)}
      icon={error ? 'atencao' : 'confirmado'}
      onClose={dismiss}
    >
      {error ? (
        <p className="text-sm">{error}</p>
      ) : (
        <div className="-ml-2 flex flex-wrap gap-1">
          <Button
            size="sm"
            variant="discreto"
            className="mt-1"
            loading={mutations.undo.isPending}
            onClick={() =>
              mutations.undo.mutate(move.passageId, {
                onSuccess: dismiss,
                onError: (e) => setError(e.message),
              })
            }
          >
            Desfazer
          </Button>
          {move.closed && <SeeClosed onSee={dismiss} />}
        </div>
      )}
    </Toast>
  );
}
